/**
 * Format phone number into correct format
 * @param phone
 * @returns {string|*}
 */
const formatPhone = phone => {
  if (!phone) {
    return '';
  } // if it's not a number (eg a masked value) then return it


  if (!Number(phone)) {
    return phone;
  }

  return phone.replace(/[^0-9]/g, '').slice(-10).replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
};

/**
 * Validate a phone number - strips all non-digits and leading 0s to ensure its 10 characters long
 * @param phone
 * @returns {boolean}
 */
const validPhone = phone => {
  return phone.replace(/\D/g, '').replace(/^0+/g, '').length === 10;
};

/**
 * Track events in Google Analytics
 * trackEventInGa('eventName', {extra: 'params'})
 * @param {String} eventName
 * @param {Object} eventProps
 */
const trackEventInGa = (eventName, eventProps = {}) => {
  // if there is no gtag defined or no event name then don't do anything
  if (window.gtag && eventName) {
    window.gtag('event', eventName, eventProps);
  }
};

/**
 * Track errors in datadog
 * trackErrorInDatadog(e, { vuex: 'module/action' })
 * @param {Error|Object} error
 * @param {Object} context
 */
const trackErrorInDatadog = (error, context) => {
  if (window.DD_RUM && window.DD_RUM.addError) {
    window.DD_RUM.addError(error, {
      error: error.response || error.message,
      ...context
    });
  }
};

/**
 * Convert cents to dollars
 * @param amount
 * @returns {string}
 */
const convertCentsToDollars = amount => {
  return (Number(amount) / 100).toFixed(2).toString();
};

/**
 * Convert dollars to cents
 * @param amount
 * @returns {number}
 */
const convertDollarsToCents = amount => {
  const value = Number.parseFloat(amount).toFixed(2);
  return Math.round(Number.parseFloat(value) * 100);
};

/**
 * Does unique property exists in array
 * @param value
 * @param array
 * @param uniqueField
 * @returns {boolean}
 */
const isUniqueProperty = (value, array, uniqueField) => {
  const totalRecordsWithValue = array.filter(v => v[uniqueField] === value).length;
  return totalRecordsWithValue === 1;
};

/**
 * Verifies if text is a valid url
 * @param {String} text
 * @returns {boolean}
 */
const isValidUrl = text => {
  const pattern = new RegExp('^(https?:\\/\\/){1}' + // protocol
  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
  '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
  '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
  '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator

  return !!pattern.test(text);
};

/**
 * Get parsed value from local storage. If not present or error occurs - fall back to the defaultValue if provided
 * @param   {String} localStorageKey
 * @param   {*}      [defaultValue=undefined]
 * @returns {*}
 */
const getLocalStorageValueParsed = (localStorageKey, defaultValue = undefined) => {
  let value = defaultValue;

  try {
    if (typeof window.localStorage !== 'undefined' && localStorageKey) {
      var _JSON$parse;

      value = (_JSON$parse = JSON.parse(window.localStorage.getItem(localStorageKey))) !== null && _JSON$parse !== void 0 ? _JSON$parse : defaultValue;
    }
  } catch (e) {
    console.error(`Could not get local storage value for key ${localStorageKey}`);
  }

  return value;
};

/**
 * Get URL search params from `location.search` or local storage
 * Usage: `getStoredUrlParams('local-storage-key').get('yourUrlSearchParamName')`
 * @param {String} localStorageKey
 * @returns {*}
 */

const getStoredUrlParams = localStorageKey => {
  const savedParams = getLocalStorageValueParsed(localStorageKey, {});
  const urlParams = new URLSearchParams(window.location.search);
  return {
    get: field => {
      return urlParams.get(field) || savedParams[field] || undefined;
    }
  };
};

export { convertCentsToDollars, convertDollarsToCents, formatPhone, getLocalStorageValueParsed, getStoredUrlParams, isUniqueProperty, isValidUrl, trackErrorInDatadog, trackEventInGa, validPhone };
